import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Sparkles, Star, X, Upload, Video, Play } from "lucide-react";
import { NAV_CATEGORIES } from "../../mock";
import {
  adminListArticles,
  adminCreateArticle,
  adminUpdateArticle,
  adminDeleteArticle,
  generateDraft,
  uploadFile,
  uploadFileChunked,
  fileUrl,
} from "../../api";

const empty = {
  id: null,
  title: "",
  excerpt: "",
  category: "Mercati",
  author: "Redazione",
  readTime: "4 min",
  featured: false,
  image: "",
  videoUrl: "",
  contentText: "",
};

const ArticleManager = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [videoProgress, setVideoProgress] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      setItems(await adminListArticles());
    } catch (e) {
      setItems([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => setForm({ ...empty });
  const openEdit = (a) =>
    setForm({
      id: a.id,
      title: a.title,
      excerpt: a.excerpt || "",
      category: a.category || "Mercati",
      author: a.author || "Redazione",
      readTime: a.readTime || "4 min",
      featured: !!a.featured,
      image: a.image || "",
      videoUrl: a.videoUrl || "",
      contentText: (a.content || []).join("\n\n"),
    });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await uploadFile(file);
      set("image", url);
    } catch (err) {
      setMsg("Errore upload immagine");
    } finally {
      setBusy(false);
    }
  };

  const onVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMsg("");
    setVideoProgress(0);
    try {
      const { url } = await uploadFileChunked(file, (p) => setVideoProgress(p));
      set("videoUrl", url);
    } catch (err) {
      setMsg("Errore upload video");
    } finally {
      setVideoProgress(null);
    }
  };

  const onGenerate = async () => {
    if (!form.title.trim()) {
      setMsg("Inserisci prima un titolo per generare con AI");
      return;
    }
    setAiBusy(true);
    setMsg("");
    try {
      const d = await generateDraft({ title: form.title, category: form.category });
      setForm((f) => ({
        ...f,
        title: d.title || f.title,
        excerpt: d.excerpt || f.excerpt,
        readTime: d.readTime || f.readTime,
        contentText: (d.content || []).join("\n\n"),
      }));
    } catch (err) {
      setMsg("Errore generazione AI (verifica il saldo della LLM key)");
    } finally {
      setAiBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    const body = {
      title: form.title,
      excerpt: form.excerpt,
      image: form.image,
      videoUrl: form.videoUrl,
      category: form.category,
      author: form.author,
      readTime: form.readTime,
      featured: form.featured,
      content: form.contentText
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean),
    };
    try {
      if (form.id) await adminUpdateArticle(form.id, body);
      else await adminCreateArticle(body);
      setForm(null);
      await load();
    } catch (err) {
      setMsg("Errore nel salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare l'articolo "${a.title}"?`)) return;
    await adminDeleteArticle(a.id);
    await load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-2xl text-sq-navy">Articoli</h2>
          <p className="text-sm text-gray-500">{items.length} articoli pubblicati</p>
        </div>
        {!form && (
          <button
            onClick={openNew}
            className="flex items-center gap-2 bg-sq-orange hover:bg-sq-orange-dark transition-colors text-white px-4 py-2.5 rounded-sm text-sm font-medium"
          >
            <Plus size={16} /> Nuovo articolo
          </button>
        )}
      </div>

      {msg && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{msg}</div>}

      {form && (
        <form onSubmit={save} className="bg-white border border-sq rounded-md p-6 mb-8">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-medium text-sq-navy">{form.id ? "Modifica articolo" : "Nuovo articolo"}</h3>
            <button type="button" onClick={() => setForm(null)} className="text-gray-400 hover:text-sq-navy">
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-sq-navy mb-1">Titolo</label>
              <input
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                required
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-sq-navy mb-1">Categoria</label>
              <select
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none bg-white"
              >
                {NAV_CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-sq-navy mb-1">Autore</label>
              <input
                value={form.author}
                onChange={(e) => set("author", e.target.value)}
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-sq-navy mb-1">Sommario</label>
              <textarea
                value={form.excerpt}
                onChange={(e) => set("excerpt", e.target.value)}
                rows={2}
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange"
              />
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-sq-navy">
                  Contenuto (separa i paragrafi con una riga vuota)
                </label>
                <button
                  type="button"
                  onClick={onGenerate}
                  disabled={aiBusy}
                  className="flex items-center gap-1.5 text-xs bg-sq-navy hover:bg-sq-orange transition-colors text-white px-3 py-1.5 rounded-sm disabled:opacity-60"
                >
                  <Sparkles size={13} /> {aiBusy ? "Generazione..." : "Genera con AI"}
                </button>
              </div>
              <textarea
                value={form.contentText}
                onChange={(e) => set("contentText", e.target.value)}
                rows={8}
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange font-mono"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-sq-navy mb-1">Immagine</label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 border border-sq rounded-sm px-3 py-2 text-sm cursor-pointer hover:border-sq-orange">
                  <Upload size={14} /> Carica
                  <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
                </label>
                {form.image && (
                  <img src={fileUrl(form.image)} alt="" className="h-12 w-20 object-cover rounded-sm border border-sq" />
                )}
              </div>
              <input
                value={form.image}
                onChange={(e) => set("image", e.target.value)}
                placeholder="oppure incolla un URL"
                className="w-full mt-2 border border-sq rounded-sm px-3 py-2 text-xs outline-none focus:border-sq-orange"
              />
            </div>

            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-sq-navy cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => set("featured", e.target.checked)}
                  className="w-4 h-4 accent-orange-500"
                />
                <Star size={15} className="text-sq-orange" /> In evidenza (homepage)
              </label>
            </div>

            <div className="md:col-span-2 border-t border-sq pt-4">
              <label className="flex items-center gap-2 text-sm font-medium text-sq-navy mb-1">
                <Video size={15} className="text-sq-orange" /> Video articolo (opzionale)
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Carica un file video (mp4, webm...). Verrà mostrato in cima all'articolo con un badge
                "Video" nel feed. L'immagine resta come anteprima/poster.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 border border-sq rounded-sm px-3 py-2 text-sm cursor-pointer hover:border-sq-orange">
                  <Upload size={14} /> Carica video
                  <input type="file" accept="video/*" onChange={onVideoUpload} className="hidden" />
                </label>
                {videoProgress !== null && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-sq-orange transition-all"
                        style={{ width: `${Math.round(videoProgress * 100)}%` }}
                      />
                    </div>
                    {Math.round(videoProgress * 100)}%
                  </div>
                )}
                {form.videoUrl && videoProgress === null && (
                  <div className="flex items-center gap-2">
                    <video
                      src={fileUrl(form.videoUrl)}
                      className="h-16 rounded-sm border border-sq"
                      muted
                    />
                    <button
                      type="button"
                      onClick={() => set("videoUrl", "")}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Rimuovi video
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={busy}
              className="bg-sq-orange hover:bg-sq-orange-dark transition-colors text-white px-5 py-2.5 rounded-sm text-sm font-medium disabled:opacity-60"
            >
              {busy ? "Salvataggio..." : "Salva articolo"}
            </button>
            <button
              type="button"
              onClick={() => setForm(null)}
              className="border border-sq text-sq-navy px-5 py-2.5 rounded-sm text-sm hover:bg-sq-light"
            >
              Annulla
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-sq rounded-md overflow-hidden">
        <div className="divide-y divide-[#e6e8ec]">
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-4 px-4 py-3 hover:bg-sq-light">
              <img src={fileUrl(a.image)} alt="" className="w-16 h-12 object-cover rounded-sm border border-sq flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wide text-sq-orange font-semibold">{a.category}</span>
                  {a.featured && <Star size={12} className="text-sq-orange fill-orange-400" />}
                  {a.videoUrl && (
                    <span className="text-[10px] bg-sq-orange text-white px-1.5 rounded flex items-center gap-0.5">
                      <Play size={9} /> Video
                    </span>
                  )}
                  {a.ai_generated && <span className="text-[10px] bg-sq-light border border-sq px-1.5 rounded">AI</span>}
                  {a.rss_imported && <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 px-1.5 rounded">RSS</span>}
                </div>
                <p className="font-medium text-sq-navy truncate">{a.title}</p>
                <span className="text-xs text-gray-400">{a.date}</span>
              </div>
              <button onClick={() => openEdit(a)} className="text-gray-400 hover:text-sq-orange p-2">
                <Pencil size={16} />
              </button>
              <button onClick={() => remove(a)} className="text-gray-400 hover:text-red-600 p-2">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="px-4 py-8 text-center text-gray-500 text-sm">Nessun articolo.</div>}
        </div>
      </div>
    </div>
  );
};

export default ArticleManager;
